import {getGraphColor, isDarkMode} from "../../helpers/dark_mode";

// Provides helpers for a 3-entry (min/max/median) series.

function clampChannel(value) {
    return Math.max(0, Math.min(255, Math.round(value)));
}

function parseHexColor(color) {
    if (typeof color !== "string") return null;
    const normalized = color.trim();
    const short = /^#([0-9a-f]{3})$/i.exec(normalized);
    if (short) {
        return short[1].split("").map((part) => parseInt(part + part, 16));
    }
    const full = /^#([0-9a-f]{6})$/i.exec(normalized);
    if (full) {
        return [
            parseInt(full[1].slice(0, 2), 16),
            parseInt(full[1].slice(2, 4), 16),
            parseInt(full[1].slice(4, 6), 16),
        ];
    }
    const rgb = /^rgba?\(([^)]+)\)$/i.exec(normalized);
    if (rgb) {
        return rgb[1].split(",").slice(0, 3).map((part) => parseFloat(part.trim()));
    }
    return null;
}

function rgba(color, alpha) {
    const channels = parseHexColor(color);
    if (!channels) return color;
    return `rgba(${clampChannel(channels[0])}, ${clampChannel(channels[1])}, ${clampChannel(channels[2])}, ${alpha})`;
}

function mixTowardWhite(color, amount = 0.18) {
    const channels = parseHexColor(color);
    if (!channels) return color;
    return `rgb(${
        clampChannel(channels[0] + (255 - channels[0]) * amount)
    }, ${
        clampChannel(channels[1] + (255 - channels[1]) * amount)
    }, ${
        clampChannel(channels[2] + (255 - channels[2]) * amount)
    })`;
}

function bandGradient(color) {
    // Dark backgrounds swallow low-alpha fills, so lift the band opacity there
    // to keep the min/max shadow readable. Light mode keeps the subtle look.
    const boost = isDarkMode() ? 2.0 : 1.0;
    return {
        type: "linear",
        x: 0,
        y: 0,
        x2: 0,
        y2: 1,
        colorStops: [
            { offset: 0, color: rgba(color, 0.03 * boost) },
            { offset: 0.22, color: rgba(color, 0.09 * boost) },
            { offset: 0.5, color: rgba(color, 0.17 * boost) },
            { offset: 0.78, color: rgba(color, 0.09 * boost) },
            { offset: 1, color: rgba(color, 0.03 * boost) },
        ],
        global: false,
    };
}

// Helper for min/max/median series
export class MinMaxSeries {
    // Build the series
    constructor(seriesName, paletteIndex) {
        this.seriesName = seriesName;
        this.paletteIndex = paletteIndex;
        const baseColor = getGraphColor(paletteIndex);
        const lineColor = mixTowardWhite(baseColor, 0.22);
        this.series = [
            // Value series
            {
                type: 'line',
                data: [],
                name: this.seriesName,
                smooth: true,
                areaStyle: { opacity: 0 },
                itemStyle: {
                    color: lineColor
                },
                lineStyle: {
                    color: lineColor,
                    width: 2.8,
                    opacity: 0.98,
                    shadowBlur: 10,
                    shadowColor: rgba(baseColor, 0.22),
                    shadowOffsetY: 0,
                },
                showSymbol: false,
                z: 8,
            },
            // Minimum
            {
                type: 'line',
                data: [],
                name: this.seriesName + " Min",
                smooth: true,
                // Hide the area
                areaStyle: { color: baseColor, opacity: 0.0 },
                itemStyle: {
                    color: baseColor
                },
                lineStyle: {
                    color: rgba(baseColor, 0.24),
                    width: 1.1,
                    opacity: 0.24,
                },
                stack: this.seriesName,
                showSymbol: false,
                z: 4,
            },
            // Maximum
            {
                type: 'line',
                data: [],
                name: this.seriesName + " Max",
                smooth: true,
                areaStyle: { color: bandGradient(baseColor), opacity: 1 },
                itemStyle: {
                    color: baseColor
                },
                lineStyle: {
                    color: rgba(baseColor, 0.2),
                    width: 1.1,
                    opacity: 0.2,
                },
                stack: this.seriesName,
                showSymbol: false,
                z: 5,
            }
        ];
    }

    // Clear the stored data
    clear() {
        this.series[0].data = [];
        this.series[1].data = [];
        this.series[2].data = [];
    }

    // Add an "upwards" band. (Typically download)
    pushPositive(median, min, max) {
        this.series[0].data.push(median);
        this.series[1].data.push(min);
        this.series[2].data.push(max - min);
    }

    // Add an inverted "downwards" band. (Typically upload)
    pushNegative(median, min, max) {
        //console.log("Pushing negative", median, min, max);
        this.series[0].data.push(0.0 - median);
        this.series[1].data.push((0.0 - min));
        this.series[2].data.push(0.0 - (max - min));
    }

    addToOptions(option) {
        option.series.push(this.series[0]);
        option.series.push(this.series[1]);
        option.series.push(this.series[2]);
    }
}
